import Toast from 'react-native-toast-message'
import { getCurrentOrgID } from './organization';
import ErrorHandler from '../utils/ErrorHandler';
import { supabase, extractEdgeFunctionError } from './client';
import { insertActivity } from './activity';
import { buildUpdateDetails } from '../utils/buildActivityDetails';

// Allowed actionType values — must match the pathsteps_actiontype_check DB constraint
const VALID_ACTION_TYPES = ['SEND_EMAIL', 'SEND_SMS', 'ENROLL_TRAINING', 'ADD_TO_SMALL_GROUP', 'POST_TO_BOARD', 'ADD_TO_ROLE', 'INVITE_TO_REACH', 'TRIGGER_PATH', 'UPDATE_PERSON', 'REGISTER_FOR_EVENT', 'CHECK_INTO_EVENT', 'RESOLVE_PERSON', 'UPSERT_PERSON', 'START_FOLLOW_UP_PATH', 'CREATE_TASK', 'WAIT_FOR_TASK', 'INVOKE_AUTOMATION', 'REQUEST_APPROVAL'];

// ─── Trigger Variables ───────────────────────────────────────────────────────
// Fetches the variable spec for a given trigger type from the path-variables
// edge function. Used by the step config UI to populate variable dropdowns.
// organizationID is resolved internally via getCurrentOrgID (matches the
// pattern used by all other data functions in this module).
const fetchPathVariables = async ({ triggerType, formID, paramType, paramSemantic }) => {
    const organizationID = await getCurrentOrgID();

    if (!organizationID) {
        ErrorHandler(Error('No current organization ID'), { mutation: 'fetchPathVariables', triggerType }, 'Something went wrong fetching trigger variables');
        return [];
    }

    const { data, error } = await supabase.functions.invoke('path-variables', {
        body: { organizationID, triggerType, formID: formID || null, paramType: paramType || null, paramSemantic: paramSemantic || null },
    });

    // extractEdgeFunctionError handles FunctionsHttpError (reads response body for
    // the real edge-fn error), FunctionsRelayError, FunctionsFetchError, and
    // application-level { error } in a 2xx body. Returns null when there's no error.
    const extracted = await extractEdgeFunctionError(error, data);
    if (extracted) {
        ErrorHandler(
            extracted.error,
            { mutation: 'fetchPathVariables', triggerType, organizationID, httpStatus: extracted.httpStatus, error: extracted.error, body: extracted.data },
            'Something went wrong fetching trigger variables',
        );
        return [];
    }

    return data?.variables || [];
};

const getPaths = async () => {
    const orgID = await getCurrentOrgID();

    const { error, data: paths } = await supabase
        .from('Paths')
        .select('*')
        .eq('organizationID', orgID)
        .order('createdAt', { ascending: false });

    if (error) {
        ErrorHandler(Error(error.message), { organizationID: orgID, error }, 'Something went wrong getting paths');
        return [];
    }

    if (!paths?.length) return [];

    const pathIDs = paths.map(p => p.id);
    const { data: steps } = await supabase
        .from('PathSteps')
        .select('id, pathID, stepType, triggerType:config->>triggerType')
        .in('pathID', pathIDs);

    const { data: enrollments } = await supabase
        .from('PathEnrollments')
        .select('id, pathID, status')
        .in('pathID', pathIDs);

    return paths.map(p => ({
        ...p,
        stepCount: steps?.filter(s => s.pathID === p.id && s.stepType !== 'TRIGGER').length || 0,
        triggerTypes: [...new Set((steps || [])
            .filter(s => s.pathID === p.id && s.stepType === 'TRIGGER')
            .map(s => s.triggerType)
            .filter(Boolean))],
        activeEnrollmentCount: enrollments?.filter(e => e.pathID === p.id && e.status === 'ACTIVE').length || 0,
    }));
}

const getPathsForFormTrigger = async () => {
    const orgID = await getCurrentOrgID();

    // Fetch paths that have a TRIGGER step with triggerType = 'FORM_ENTRY'
    const { error, data: steps } = await supabase
        .from('PathSteps')
        .select(`pathID,
            Path:Paths!PathSteps_pathID_fkey(id, name, description, isActive, createdAt)
        `)
        .eq('organizationID', orgID)
        .eq('stepType', 'TRIGGER')
        .eq('config->>triggerType', 'FORM_ENTRY');

    if (error) {
        ErrorHandler(Error(error.message), { organizationID: orgID, error }, 'Something went wrong getting form-trigger paths');
        return [];
    }

    if (!steps?.length) return [];

    // De-duplicate by pathID (in case of multiple trigger steps) and filter active paths
    const seen = new Set();
    const paths = [];
    for (const s of steps) {
        const p = s.Path;
        if (!p || !p.isActive || seen.has(p.id)) continue;
        seen.add(p.id);
        paths.push(p);
    }

    return paths;
}

const getPathsForEventCheckInTrigger = async () => {
    const orgID = await getCurrentOrgID();

    // Fetch paths that have a TRIGGER step with triggerType = 'EVENT_CHECK_IN'
    const { error, data: steps } = await supabase
        .from('PathSteps')
        .select(`pathID,
            Path:Paths!PathSteps_pathID_fkey(id, name, description, isActive, createdAt)
        `)
        .eq('organizationID', orgID)
        .eq('stepType', 'TRIGGER')
        .eq('config->>triggerType', 'EVENT_CHECK_IN');

    if (error) {
        ErrorHandler(Error(error.message), { organizationID: orgID, error }, 'Something went wrong getting event-check-in paths');
        return [];
    }

    if (!steps?.length) return [];

    // De-duplicate by pathID (in case of multiple trigger steps) and filter active paths
    const seen = new Set();
    const paths = [];
    for (const s of steps) {
        const p = s.Path;
        if (!p || !p.isActive || seen.has(p.id)) continue;
        seen.add(p.id);
        paths.push(p);
    }

    return paths;
}

const getEventsForCheckInPath = async ({ pathID }) => {
    const { data, error } = await supabase
        .from('EventInstances')
        .select('id, eventID, startTime, Event:Events!EventInstances_eventID_fkey(id, name)')
        .eq('checkInPathID', pathID)
        .order('startTime', { ascending: false });

    if (error) {
        ErrorHandler(Error(error.message), { pathID, error }, 'Something went wrong getting events for this check-in path');
        return [];
    }

    if (!data?.length) return [];

    // Deduplicate by eventID — show each event once, with the most recent instance's startTime
    const seen = new Set();
    const events = [];
    for (const instance of data) {
        const event = instance.Event;
        if (!event || seen.has(event.id)) continue;
        seen.add(event.id);
        events.push({ id: event.id, name: event.name, startTime: instance.startTime });
    }

    return events;
}

const getPathsForManualTrigger = async () => {
    const orgID = await getCurrentOrgID();

    // Fetch paths that have a TRIGGER step with triggerType = 'MANUAL'
    const { error, data: steps } = await supabase
        .from('PathSteps')
        .select(`pathID,
            Path:Paths!PathSteps_pathID_fkey(id, name, description, isActive, createdAt)
        `)
        .eq('organizationID', orgID)
        .eq('stepType', 'TRIGGER')
        .eq('config->>triggerType', 'MANUAL');

    if (error) {
        ErrorHandler(Error(error.message), { organizationID: orgID, error }, 'Something went wrong getting manual-trigger paths');
        return [];
    }

    if (!steps?.length) return [];

    // De-duplicate by pathID (in case of multiple trigger steps) and filter active paths
    const seen = new Set();
    const paths = [];
    for (const s of steps) {
        const p = s.Path;
        if (!p || !p.isActive || seen.has(p.id)) continue;
        seen.add(p.id);
        paths.push(p);
    }

    return paths;
}

const getPath = async (pathID) => {
    const orgID = await getCurrentOrgID();

    const { error, data } = await supabase
        .from('Paths')
        .select(`id, name, description, isActive, createdAt, updatedAt, createdBy,
            schemaVersion, compatibilityMode, revision, inputSchema,
            Steps:PathSteps(*),
            Edges:PathStepEdges(*),
            Enrollments:PathEnrollments(*, Person:People!PathEnrollments_personID_fkey(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey), EnrollmentSteps:PathEnrollmentSteps(id, status, pathStepID, currentAssigneePosition, taskInstanceID, errorMsg, scheduledFor, activatedAt, completedAt, definitionRevision, waitState, output, result, TaskInstance:TaskInstances(id, Assignees:TaskInstancePeople(assignmentResponse, position, createdAt, Person:People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey))), PathStep:PathSteps(name, stepType, actionType))),
            CreatedBy:People!Paths_createdBy_fkey(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey),
            Recurrence:PathRecurrence(*, Role:Roles!PathRecurrence_roleID_fkey(id, name))
        `)
        .eq('organizationID', orgID)
        .eq('id', pathID)
        .order('position', { referencedTable: 'PathSteps', ascending: true })
        .order('position', { referencedTable: 'PathStepEdges', ascending: true })
        .order('enrolledAt', { referencedTable: 'PathEnrollments', ascending: false })
        .maybeSingle();

    if (error) {
        ErrorHandler(Error(error.message), { organizationID: orgID, pathID, error }, 'Something went wrong getting that path');
        throw Error();
    }

    return data;
}

const savePathRecurrence = async ({ pathID, roleID, firstOccurrenceAt, rruleString, recurrenceEndDate, isActive }) => {
    const orgID = await getCurrentOrgID();

    // If the first occurrence is in the past, point the runner at "now" so it picks the
    // next future occurrence without generating a backlog of missed occurrences.
    const firstAt = firstOccurrenceAt ? new Date(firstOccurrenceAt).toISOString() : null;
    const nextAt = firstAt && new Date(firstAt) > new Date() ? firstAt : new Date().toISOString();

    const { error } = await supabase
        .from('PathRecurrence')
        .upsert({
            organizationID: orgID,
            pathID,
            roleID,
            firstOccurrenceAt: firstAt,
            rruleString,
            recurrenceEndDate: recurrenceEndDate || null,
            nextOccurrenceAt: nextAt,
            isActive: isActive ?? true,
            updatedAt: new Date().toISOString(),
        }, { onConflict: 'pathID' });

    if (error) {
        ErrorHandler(Error(error.message), { mutation: 'savePathRecurrence', pathID, error }, 'Something went wrong saving that schedule');
        throw Error();
    }

    Toast.show({ type: 'success', text2: 'Schedule saved!' });
}

const savePath = async ({ id, name, description, isActive, createdBy, originalValues, triggerType = 'MANUAL' }) => {
    const orgID = await getCurrentOrgID();

    const payload = {
        organizationID: orgID,
        name,
        description: description || null,
        isActive: isActive ?? true,
        updatedAt: new Date().toISOString(),
    };

    let result;
    if (id) {
        const { error, data } = await supabase.from('Paths').update(payload).eq('id', id).select().maybeSingle();
        if (error) {
            ErrorHandler(Error(error.message), { mutation: 'savePath', id, error }, 'Something went wrong saving that path');
            throw Error();
        }
        result = data;
    } else {
        payload.createdBy = createdBy || null;
        const { error, data } = await supabase.from('Paths').insert(payload).select().maybeSingle();
        if (error) {
            ErrorHandler(Error(error.message), { mutation: 'savePath', error }, 'Something went wrong creating that path');
            throw Error();
        }
        result = data;

        // Auto-create trigger step for new paths
        await supabase.from('PathSteps').insert({
            organizationID: orgID,
            pathID: result.id,
            position: 0,
            name: 'Trigger',
            stepType: 'TRIGGER',
            config: { triggerType, triggerConfig: {} },
            advanceOn: 'AUTOMATIC',
        });
    }

    Toast.show({ type: 'success', text2: id ? 'Automation updated successfully!' : 'Automation created successfully!' });

    if (id) {
        const details = originalValues ? buildUpdateDetails(originalValues, payload, {
            name: { label: 'Name' },
            description: { label: 'Description' },
            isActive: { label: 'Active' },
        }) : null;
        insertActivity({ subjectType: 'Path', subjectID: id, subjectName: result.name, verb: 'update', details }).catch(() => {});
    } else {
        insertActivity({ subjectType: 'Path', subjectID: result.id, subjectName: result.name, verb: 'create' }).catch(() => {});
    }

    return result;
}

// Deep-copy a path (steps, edges, TASK-step assignment pools). Runs in a
// duplicate_path RPC so the whole copy is atomic and edges remap correctly.
// The copy is created inactive so its TRIGGER steps can't fire until edited.
const duplicatePath = async ({ pathID }) => {
    const orgID = await getCurrentOrgID();

    // duplicate_path stamps createdBy via person_id(), which prefers the JWT's
    // app_metadata.personID and falls back to RegisteredUsersOrganizations.
    // Paths.createdBy FKs to People.id, so resolve the same value here and bail
    // when it has no People row (auth user without a person record).
    // See CHURCH-APP-1B7.
    const { data: { user } } = await supabase.auth.getUser();
    let creatorPersonID = user?.app_metadata?.personID || null;
    if (!creatorPersonID) {
        const { data: regUser } = await supabase
            .from('RegisteredUsersOrganizations')
            .select('personID')
            .eq('userID', user?.id)
            .eq('organizationID', orgID)
            .maybeSingle();
        creatorPersonID = regUser?.personID || null;
    }

    if (creatorPersonID) {
        const { data: creatorPerson } = await supabase.from('People').select('id').eq('id', creatorPersonID).maybeSingle();
        if (!creatorPerson) creatorPersonID = null;
    }

    if (!creatorPersonID) {
        Toast.show({ type: 'error', text2: 'Could not find your user profile' });
        return null;
    }

    const { data, error } = await supabase.rpc('duplicate_path', {
        p_path_id: pathID,
    });

    if (error) {
        ErrorHandler(Error(error.message), { mutation: 'duplicatePath', pathID, organizationID: orgID, error }, 'Something went wrong duplicating that path');
        throw Error();
    }

    const { data: newPath } = await supabase.from('Paths').select('id, name').eq('id', data).maybeSingle();

    Toast.show({ type: 'success', text2: 'Automation duplicated!' });

    insertActivity({ subjectType: 'Path', subjectID: data, subjectName: newPath?.name, verb: 'create', details: 'Duplicated from an existing path' }).catch(() => {});

    return data;
}

const deletePath = async ({ pathID }) => {
    const { data: existing } = await supabase.from('Paths').select('name').eq('id', pathID).maybeSingle();

    const { error } = await supabase.from('Paths').delete().eq('id', pathID);

    if (error) {
        ErrorHandler(Error(error.message), { mutation: 'deletePath', pathID, error }, 'Something went wrong deleting that path');
        throw Error();
    }

    Toast.show({ type: 'success', text2: 'Automation has been deleted successfully!' });

    insertActivity({ subjectType: 'Path', subjectID: pathID, subjectName: existing?.name, verb: 'delete' }).catch(() => {});
}

// Sync the per-path assignment pool for a TASK step. Members are stored on
// TaskAssignmentMembers scoped to (taskID, pathID) — each path using the same
// task can have its own pool. `members` is an array of person objects
// (from the people SheetSelector) with at least an `id`.
const syncPathStepAssignmentMembers = async (pathID, taskID, members, orgID) => {
    if (!pathID || !taskID) return;
    const { data: existing } = await supabase.from('TaskAssignmentMembers')
        .select('id, personID, position')
        .eq('pathID', pathID)
        .eq('taskID', taskID);
    const existingByPerson = new Map((existing || []).map(m => [m.personID, m]));
    const newMemberIDs = (members || []).map(m => m.id || m.personID).filter(Boolean);
    const newMemberSet = new Set(newMemberIDs);

    const toDelete = (existing || []).filter(m => !newMemberSet.has(m.personID));
    const toAdd = (members || []).filter(m => {
        const pid = m.id || m.personID;
        return pid && !existingByPerson.has(pid);
    });

    if (toDelete.length > 0) {
        await supabase.from('TaskAssignmentMembers').delete().in('id', toDelete.map(m => m.id));
    }
    if (toAdd.length > 0) {
        const rows = toAdd.map((m, i) => ({
            pathID,
            taskID,
            personID: m.id || m.personID,
            organizationID: orgID,
            position: (i + 1) * 1000,
        }));
        await supabase.from('TaskAssignmentMembers').insert(rows);
    }
};

const savePathStep = async ({ id, pathID, position, name, description, stepType, actionType, config, isEnabled, advanceOn, assignmentMembers }) => {
    const orgID = await getCurrentOrgID();

    // Sanitize actionType: must be a valid value when stepType is ACTION, null otherwise.
    // Guards against stale form state / empty strings that would violate pathsteps_actiontype_check.
    const sanitizedActionType = stepType === 'ACTION'
        ? (actionType && VALID_ACTION_TYPES.includes(actionType) ? actionType : 'SEND_EMAIL')
        : null;

    const payload = {
        organizationID: orgID,
        pathID,
        position: position || 0,
        name: name || null,
        description: description || null,
        stepType,
        actionType: sanitizedActionType,
        config: config || {},
        isEnabled: isEnabled ?? true,
        advanceOn: advanceOn || 'AUTOMATIC',
    };

    let result;
    if (id) {
        const { error, data } = await supabase.from('PathSteps').update(payload).eq('id', id).select().maybeSingle();
        if (error) {
            ErrorHandler(Error(error.message), { mutation: 'savePathStep', id, error }, 'Something went wrong saving that step');
            throw Error();
        }
        result = data;
    } else {
        const { error, data } = await supabase.from('PathSteps').insert(payload).select().maybeSingle();
        if (error) {
            ErrorHandler(Error(error.message), { mutation: 'savePathStep', error }, 'Something went wrong creating that step');
            throw Error();
        }
        result = data;
    }

    // For TASK steps, sync the per-path assignment pool. Only sync when a
    // taskID is resolved (skipped for ASSIGN_ENROLLEE which needs no pool, and
    // for incomplete configs). assignmentMembers may be undefined when the
    // editor didn't touch the pool — only sync when explicitly provided.
    if (stepType === 'TASK' && assignmentMembers !== undefined) {
        const taskID = config?.taskID || config?.task?.id || null;
        if (taskID) {
            await syncPathStepAssignmentMembers(pathID, taskID, assignmentMembers, orgID);
        }
    }

    Toast.show({ type: 'success', text2: id ? 'Step updated!' : 'Step added!' });
    return result;
}

const deletePathStep = async ({ stepID }) => {
    const orgID = await getCurrentOrgID();

    // 1. Find all incoming edges (predecessors) and outgoing edges (successors)
    const { data: incomingEdges, error: inErr } = await supabase
        .from('PathStepEdges')
        .select('id, pathID, sourceStepID, targetStepID, branchID, branchLabel, position, config')
        .eq('targetStepID', stepID);

    const { data: outgoingEdges, error: outErr } = await supabase
        .from('PathStepEdges')
        .select('id, pathID, sourceStepID, targetStepID, branchID, branchLabel, position, config')
        .eq('sourceStepID', stepID);

    if (inErr || outErr) {
        ErrorHandler(Error((inErr || outErr).message), { mutation: 'deletePathStep', stepID, error: inErr || outErr }, 'Something went wrong fetching edges');
        throw Error();
    }

    // 2. Compute reconnection edges: for each predecessor → successor pair,
    //    create a new edge preserving the predecessor's branch info (so branch
    //    flows stay intact). Filter out edges that would collide with the
    //    predecessor's EXISTING outgoing edges (to non-deleted steps) — the
    //    unique constraint pathstepedges_unique_source_branch allows only one
    //    outgoing edge per (path, source, branch). See CHURCH-APP-17N.
    let newEdges = [];
    if (incomingEdges?.length && outgoingEdges?.length) {
        // Fetch existing outgoing edges from each predecessor (excluding edges
        // to the step being deleted, which will be cascade-deleted).
        const predecessorIDs = [...new Set(incomingEdges.map(e => e.sourceStepID))];
        const { data: existingOutgoing } = await supabase
            .from('PathStepEdges')
            .select('sourceStepID, branchID')
            .in('sourceStepID', predecessorIDs)
            .neq('targetStepID', stepID);

        // Build a set of (sourceStepID, branchID) pairs that already have an
        // outgoing edge to a non-deleted step.
        const existingSlots = new Set(
            (existingOutgoing || []).map(e => `${e.sourceStepID}:${e.branchID || 'default'}`)
        );

        const seen = new Set();
        for (const inEdge of incomingEdges) {
            for (const outEdge of outgoingEdges) {
                // Don't create self-loops
                if (inEdge.sourceStepID === outEdge.targetStepID) continue;
                const key = `${inEdge.sourceStepID}:${inEdge.branchID || 'default'}`;
                if (seen.has(key)) continue;
                // Skip if predecessor already has an outgoing edge on this branch
                // to a different (non-deleted) step — can't have two.
                if (existingSlots.has(key)) continue;
                seen.add(key);
                newEdges.push({
                    organizationID: orgID,
                    pathID: inEdge.pathID,
                    sourceStepID: inEdge.sourceStepID,
                    targetStepID: outEdge.targetStepID,
                    branchID: inEdge.branchID || null,
                    branchLabel: inEdge.branchLabel || null,
                    position: inEdge.position || 0,
                    config: inEdge.config || {},
                });
            }
        }
    }

    // 3. Delete the step FIRST (FK CASCADE removes all edges to/from it).
    //    Must happen before inserting reconnection edges — otherwise the
    //    predecessor's old outgoing edge to the deleted step still occupies
    //    the (source, branch) slot and the unique constraint blocks the insert.
    const { error } = await supabase.from('PathSteps').delete().eq('id', stepID);

    if (error) {
        ErrorHandler(Error(error.message), { mutation: 'deletePathStep', stepID, error }, 'Something went wrong deleting that step');
        throw Error();
    }

    // 4. Insert reconnection edges now that old edges are cascade-deleted.
    if (newEdges.length > 0) {
        const { error: insertErr } = await supabase.from('PathStepEdges').insert(newEdges);
        if (insertErr) {
            ErrorHandler(Error(insertErr.message), { mutation: 'deletePathStep', stepID, error: insertErr }, 'Something went wrong reconnecting path edges');
            throw Error();
        }
    }

    Toast.show({ type: 'success', text2: 'Step removed!' });
}

const reorderPathSteps = async (steps) => {
    const updates = steps.map((step, index) =>
        supabase.from('PathSteps').update({ position: index + 1 }).eq('id', step.id)
    );

    const results = await Promise.all(updates);
    const failed = results.find(r => r.error);
    if (failed) {
        ErrorHandler(Error(failed.error.message), { mutation: 'reorderPathSteps', error: failed.error }, 'Something went wrong reordering steps');
        throw Error();
    }
}

const savePathStepPositions = async (steps) => {
    const updates = steps.map((step) =>
        supabase.from('PathSteps').update({ position: step.position }).eq('id', step.id)
    );

    const results = await Promise.all(updates);
    const failed = results.find(r => r.error);
    if (failed) {
        ErrorHandler(Error(failed.error.message), { mutation: 'savePathStepPositions', error: failed.error }, 'Something went wrong saving step positions');
        throw Error();
    }
}

// ─── PathStepEdges ────────────────────────────────────────────────────────────

const savePathStepEdge = async ({ id, pathID, sourceStepID, targetStepID, branchID, branchLabel, position, config }) => {
    const orgID = await getCurrentOrgID();

    const payload = {
        organizationID: orgID,
        pathID,
        sourceStepID,
        targetStepID,
        branchID: branchID || null,
        branchLabel: branchLabel || null,
        position: position || 0,
        config: config || {},
    };

    let result;
    if (id) {
        const { error, data } = await supabase.from('PathStepEdges').update(payload).eq('id', id).select().maybeSingle();
        if (error) {
            ErrorHandler(Error(error.message), { mutation: 'savePathStepEdge', id, error }, 'Something went wrong saving that edge');
            throw Error();
        }
        result = data;
    } else {
        const slot = branchID || null;
        let slotQuery = supabase.from('PathStepEdges').select('id').eq('pathID', pathID).eq('sourceStepID', sourceStepID);
        slotQuery = slot ? slotQuery.eq('branchID', slot) : slotQuery.is('branchID', null);
        const { data: existing } = await slotQuery.maybeSingle();
        if (existing) {
            Toast.show({ type: 'error', text1: 'Output already connected', text2: 'That step output already leads somewhere.' });
            throw Error();
        }
        const { error, data } = await supabase.from('PathStepEdges').insert(payload).select().maybeSingle();
        if (error) {
            // 23505 = unique constraint violation on pathstepedges_unique_source_branch.
            // The pre-check above handles the common case, but a TOCTOU race (rapid
            // double-action) can still hit the DB constraint. Show the same toast
            // instead of reporting to Sentry. See CHURCH-APP-17P.
            if (error.code === '23505') {
                Toast.show({ type: 'error', text1: 'Output already connected', text2: 'That step output already leads somewhere.' });
                throw Error();
            }
            ErrorHandler(Error(error.message), { mutation: 'savePathStepEdge', error }, 'Something went wrong creating that edge');
            throw Error();
        }
        result = data;
    }

    return result;
}

const deletePathStepEdge = async ({ edgeID }) => {
    const { error } = await supabase.from('PathStepEdges').delete().eq('id', edgeID);

    if (error) {
        ErrorHandler(Error(error.message), { mutation: 'deletePathStepEdge', edgeID, error }, 'Something went wrong deleting that edge');
        throw Error();
    }
}

// ─── Canvas positions ─────────────────────────────────────────────────────────

const saveCanvasPositions = async ({ stepPositions, edgePositions }) => {
    const updates = [];

    if (stepPositions?.length) {
        for (const { id, config } of stepPositions) {
            updates.push(
                supabase.from('PathSteps')
                    .update({ config })
                    .eq('id', id)
            );
        }
    }

    if (edgePositions?.length) {
        for (const { id, config } of edgePositions) {
            updates.push(
                supabase.from('PathStepEdges')
                    .update({ config })
                    .eq('id', id)
            );
        }
    }

    if (!updates.length) return;

    const results = await Promise.all(updates);
    const failed = results.find(r => r.error);
    if (failed) {
        ErrorHandler(Error(failed.error.message), { mutation: 'saveCanvasPositions', error: failed.error }, 'Something went wrong saving canvas positions');
    }
}

// ─── Enrollments ──────────────────────────────────────────────────────────────

const getPathEnrollments = async (pathID) => {
    const orgID = await getCurrentOrgID();

    const { error, data } = await supabase
        .from('PathEnrollments')
        .select(`*,
            Person:People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey),
            EnrolledByPerson:People!PathEnrollments_enrolledBy_fkey(id, firstName, lastName),
            Steps:PathEnrollmentSteps(*, PathStep:PathSteps(name, stepType, actionType, position))
        `)
        .eq('organizationID', orgID)
        .eq('pathID', pathID)
        .order('enrolledAt', { ascending: false });

    if (error) {
        ErrorHandler(Error(error.message), { organizationID: orgID, pathID, error }, 'Something went wrong getting enrollments');
        return [];
    }

    return data;
}

const getPersonPathEnrollments = async (personID) => {
    const orgID = await getCurrentOrgID();

    const { error, data } = await supabase
        .from('PathEnrollments')
        .select(`*, Path:Paths(id, name, isActive)`)
        .eq('organizationID', orgID)
        .eq('personID', personID)
        .order('enrolledAt', { ascending: false });

    if (error) {
        ErrorHandler(Error(error.message), { organizationID: orgID, personID, error }, 'Something went wrong getting person enrollments');
        return [];
    }

    return data;
}

const enrollPerson = async ({ pathID, personID, enrolledBy, triggerContext }) => {
    const { error, data } = await supabase.rpc('start_path_enrollment', {
        p_path_id: pathID,
        p_person_id: personID,
        p_enrolled_by: enrolledBy || null,
        p_trigger_context: triggerContext || { triggerType: 'MANUAL' },
    });

    if (error) {
        ErrorHandler(Error(error.message), { mutation: 'enrollPerson', pathID, personID, error }, 'Something went wrong enrolling that person');
        throw Error();
    }

    Toast.show({ type: 'success', text2: 'Run started for person!' });
    return data;
}

const enrollRole = async ({ pathID, roleID, enrolledBy }) => {
    const { error, data } = await supabase.rpc('start_path_enrollment_for_role', {
        p_path_id: pathID,
        p_role_id: roleID,
        p_enrolled_by: enrolledBy || null,
        p_trigger_context: { triggerType: 'MANUAL' },
    });

    if (error) {
        ErrorHandler(Error(error.message), { mutation: 'enrollRole', pathID, roleID, error }, 'Something went wrong enrolling that role');
        throw Error();
    }

    Toast.show({ type: 'success', text2: data ? `${data} runs started!` : 'Role run started!' });
    return data;
}

const cancelEnrollment = async ({ enrollmentID }) => {
    const { error } = await supabase
        .from('PathEnrollments')
        .update({ status: 'CANCELLED', cancelledAt: new Date().toISOString() })
        .eq('id', enrollmentID);

    if (error) {
        ErrorHandler(Error(error.message), { mutation: 'cancelEnrollment', enrollmentID, error }, 'Something went wrong cancelling that enrollment');
        throw Error();
    }

    Toast.show({ type: 'success', text2: 'Run cancelled!' });
}

const manuallyAdvanceStep = async ({ enrollmentStepID, completedBy }) => {
    const { error } = await supabase.rpc('manually_advance_step', {
        p_enrollment_step_id: enrollmentStepID,
        p_completed_by: completedBy,
    });

    if (error) {
        ErrorHandler(Error(error.message), { mutation: 'manuallyAdvanceStep', enrollmentStepID, error }, 'Something went wrong advancing that step');
        throw Error();
    }

    Toast.show({ type: 'success', text2: 'Step advanced!' });
}

const skipDelayStep = async ({ enrollmentStepID, completedBy }) => {
    const { error } = await supabase.rpc('skip_delay_step', {
        p_enrollment_step_id: enrollmentStepID,
        p_completed_by: completedBy,
    });

    if (error) {
        ErrorHandler(Error(error.message), { mutation: 'skipDelayStep', enrollmentStepID, error }, 'Something went wrong skipping that delay');
        throw Error();
    }

    Toast.show({ type: 'success', text2: 'Delay skipped!' });
}

const pauseEnrollment = async ({ enrollmentID }) => {
    // Find the current step that has a queued message
    const { data: steps, error: stepsError } = await supabase
        .from('PathEnrollmentSteps')
        .select('id, queueMsgId, status')
        .eq('pathEnrollmentID', enrollmentID)
        .in('status', ['WAITING', 'WAITING_MANUAL', 'IN_PROGRESS', 'PENDING']);

    if (stepsError) {
        ErrorHandler(Error(stepsError.message), { mutation: 'pauseEnrollment', enrollmentID, error: stepsError }, 'Something went wrong pausing that enrollment');
        throw Error();
    }

    // Delete queue messages for all active steps
    for (const step of (steps || [])) {
        if (step.queueMsgId) {
            await supabase.rpc('delete_paths_message', { p_msg_id: step.queueMsgId });
        }
    }

    // Set enrollment status to PAUSED
    const { error } = await supabase
        .from('PathEnrollments')
        .update({ status: 'PAUSED' })
        .eq('id', enrollmentID);

    if (error) {
        ErrorHandler(Error(error.message), { mutation: 'pauseEnrollment', enrollmentID, error }, 'Something went wrong pausing that enrollment');
        throw Error();
    }

    Toast.show({ type: 'success', text2: 'Execution paused' });
}

const resumeEnrollment = async ({ enrollmentID }) => {
    // Set enrollment status to ACTIVE
    const { error } = await supabase
        .from('PathEnrollments')
        .update({ status: 'ACTIVE' })
        .eq('id', enrollmentID);

    if (error) {
        ErrorHandler(Error(error.message), { mutation: 'resumeEnrollment', enrollmentID, error }, 'Something went wrong resuming that enrollment');
        throw Error();
    }

    // Find ALL resumable steps and re-enqueue each — enrollments with parallel
    // branches can have several in-flight steps. PathEnrollmentSteps has no
    // createdAt column — order by activatedAt. See CHURCH-APP-16Q.
    const { data: stepsToResume, error: stepError } = await supabase
        .from('PathEnrollmentSteps')
        .select('id')
        .eq('pathEnrollmentID', enrollmentID)
        .in('status', ['WAITING', 'WAITING_MANUAL', 'IN_PROGRESS', 'PENDING'])
        .order('activatedAt', { ascending: true, nullsFirst: false });

    if (stepError) {
        ErrorHandler(Error(stepError.message), { mutation: 'resumeEnrollment', enrollmentID, error: stepError }, 'Something went wrong resuming that enrollment');
        throw Error();
    }

    for (const step of (stepsToResume || [])) {
        const { error: enqueueError } = await supabase.rpc('enqueue_path_step', {
            p_enrollment_step_id: step.id,
        });
        if (enqueueError) {
            // Log but keep going — a failure on one branch shouldn't prevent the
            // other resumable steps from being re-enqueued.
            ErrorHandler(Error(enqueueError.message), { mutation: 'resumeEnrollment', enrollmentID, enrollmentStepID: step.id, error: enqueueError }, 'Something went wrong resuming that enrollment');
        }
    }

    Toast.show({ type: 'success', text2: 'Execution resumed' });
}

// Reset a FAILED PathEnrollmentSteps row to PENDING, clear its errorMsg, and
// re-enqueue it. Server-side RPC — errors surface as PostgREST errors.
const retryPathStep = async ({ enrollmentStepID }) => {
    const { error } = await supabase.rpc('retry_path_step', {
        p_enrollment_step_id: enrollmentStepID,
    });

    if (error) {
        ErrorHandler(Error(error.message), { mutation: 'retryPathStep', enrollmentStepID, error }, 'Something went wrong retrying that step');
        throw Error();
    }

    Toast.show({ type: 'success', text2: 'Step retrying!' });
}

const insertStepOnEdge = async ({ edgeID, newStepID }) => {
    const { error } = await supabase
        .rpc('insert_path_step_on_edge', {
            p_edge_id: edgeID,
            p_new_step_id: newStepID,
        });

    if (error) {
        ErrorHandler(Error(error.message), { mutation: 'insertStepOnEdge', edgeID, newStepID, error }, 'Something went wrong inserting the step');
        throw Error();
    }
}

const moveStepOntoEdge = async ({ stepID, edgeID }) => {
    const { error } = await supabase.rpc('move_path_step_onto_edge', {
        p_step_id: stepID,
        p_edge_id: edgeID,
    });

    if (error) {
        ErrorHandler(Error(error.message), { mutation: 'moveStepOntoEdge', stepID, edgeID, error }, 'Something went wrong moving the step');
        throw Error();
    }
}

const swapAdjacentSteps = async ({ upperStepID, lowerStepID }) => {
    const { error } = await supabase.rpc('swap_adjacent_path_steps', {
        p_upper_step_id: upperStepID,
        p_lower_step_id: lowerStepID,
    });

    if (error) {
        ErrorHandler(Error(error.message), { mutation: 'swapAdjacentSteps', upperStepID, lowerStepID, error }, error.message || 'Could not swap steps');
        throw Error();
    }
}

const detachPathStep = async ({ stepID }) => {
    const { error } = await supabase.rpc('detach_path_step', { p_step_id: stepID });

    if (error) {
        ErrorHandler(Error(error.message), { mutation: 'detachPathStep', stepID, error }, 'Something went wrong detaching the step');
        throw Error();
    }
}

const deleteEnrollment = async ({ enrollmentID }) => {
    // Find all steps with queue messages and delete them
    const { data: steps, error: stepsError } = await supabase
        .from('PathEnrollmentSteps')
        .select('id, queueMsgId')
        .eq('pathEnrollmentID', enrollmentID)
        .not('queueMsgId', 'is', null);

    if (stepsError) {
        ErrorHandler(Error(stepsError.message), { mutation: 'deleteEnrollment', enrollmentID, error: stepsError }, 'Something went wrong deleting that enrollment');
        throw Error();
    }

    for (const step of (steps || [])) {
        if (step.queueMsgId) {
            await supabase.rpc('delete_paths_message', { p_msg_id: step.queueMsgId });
        }
    }

    // Delete the enrollment (cascade will handle steps)
    const { error } = await supabase
        .from('PathEnrollments')
        .delete()
        .eq('id', enrollmentID);

    if (error) {
        ErrorHandler(Error(error.message), { mutation: 'deleteEnrollment', enrollmentID, error }, 'Something went wrong deleting that enrollment');
        throw Error();
    }

    Toast.show({ type: 'success', text2: 'Run deleted' });
}

const getEnrollmentContextByTaskInstance = async (taskInstanceID) => {
    if (!taskInstanceID) return null;
    const { data, error } = await supabase
        .from('PathEnrollmentSteps')
        .select('*, PathStep:PathSteps(name, description, config, actionType), PathEnrollment:PathEnrollments(id, personID, pathID, Path:Paths!PathEnrollments_pathID_fkey(id, name), Person:People!PathEnrollments_personID_fkey(id, firstName, lastName, email, avatarUpdatedAt, avatarStorageKey, Phones:PeoplePhones(phoneNumber, label, smsOptInStatus, private)))')
        .eq('taskInstanceID', taskInstanceID)
        .limit(1)
        .maybeSingle();
    if (error) {
        return null;
    }
    return data;
};

export {
    getPaths,
    getPathsForFormTrigger,
    getPathsForEventCheckInTrigger,
    getEventsForCheckInPath,
    getPathsForManualTrigger,
    getPath,
    fetchPathVariables,
    savePath,
    savePathRecurrence,
    duplicatePath,
    deletePath,
    savePathStep,
    syncPathStepAssignmentMembers,
    deletePathStep,
    reorderPathSteps,
    savePathStepPositions,
    savePathStepEdge,
    deletePathStepEdge,
    insertStepOnEdge,
    moveStepOntoEdge,
    swapAdjacentSteps,
    detachPathStep,
    saveCanvasPositions,
    getPathEnrollments,
    getPersonPathEnrollments,
    getEnrollmentContextByTaskInstance,
    enrollPerson,
    enrollRole,
    cancelEnrollment,
    pauseEnrollment,
    resumeEnrollment,
    retryPathStep,
    deleteEnrollment,
    manuallyAdvanceStep,
    skipDelayStep,
}
